import React from 'react'
import Router from 'next/router'
import Link from 'next/link'
import HeadTitle from '../components/head_title'
import AuthService from '../service/auth'
import Api from '../service/api'
import Layout from '../components/layout/layout'
import {extractAuthError, isAxiosAuthError} from '../utils/error'
import TimeoutAlert from '../components/timeout_alert'
import { genericTextChange, genericCheckboxChange } from '../utils/forms'
import Breadcrumbs from '../components/breadcrumbs'
import Loading from '../components/loading'
import styles from '../styles/sign_in.module.scss'
import { useDispatch, useSelector } from "react-redux";
import {actions} from "../store/actions";
import {RootState} from "../store/reducers/reducers";
import { get as getLoginRedirectPath, clear as clearLoginRedirectPath } from "../service/post_login_redirect_path";

function SignIn() {
	const [email, setEmail] = React.useState('');
	const [password, setPassword] = React.useState('');
  const [serverError, setServerError] = React.useState<React.ReactNode | string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [rememberMe, setRememberMe] = React.useState(false);
  const [showLoading, setShowLoading] = React.useState(false);

  const signedIn = AuthService.useIsLoggedIn();
  const tfaPassed = AuthService.useHasCompletedTfa();

  const dispatch = useDispatch();
  const signInAsyncError = useSelector((state: RootState) => state.user.signInAsyncError);

  React.useEffect(() => {
    if (!signedIn) {
      return;
    }

    if (!tfaPassed) {
      Router.replace(`/two_factor_auth/verify`);
    } else {
      const postLoginPath = getLoginRedirectPath();
      if (postLoginPath) {
        Router.replace(postLoginPath);
        clearLoginRedirectPath();
      } else {
        Router.replace('/');
      }
    }
  }, [signedIn, tfaPassed])

  const handleResendVerificationLink = React.useCallback(() => {
    Api.auth.resendVerifyCode(email)
      .then(() => {
        setSuccessMessage('Successfully resent verification link.')
      })
      .catch((error) => {
        const errorMessage = extractAuthError(error)
        setServerError(errorMessage)
      })
  }, [email]);

  React.useEffect(() => {
    if (!signInAsyncError) {
      return;
    }

    let errorMessage = extractAuthError(signInAsyncError);
    if (isAxiosAuthError(signInAsyncError)) {
      if (signInAsyncError.response && signInAsyncError.response.data.errorCode === 'auth/unverified_email') {
        setServerError(<>
          {errorMessage}.
          <button className={`btn btn-link ${styles.btnResendVerification}`} onClick={handleResendVerificationLink}>Resend Verification Link</button>
        </>);
      } else {
        setServerError(errorMessage);
      }
    } else {
      setServerError(errorMessage);
    }

    dispatch(actions.user.setSignInAsyncError(null));
    setShowLoading(false);
  }, [signInAsyncError]);

	const handleSignIn = React.useCallback((e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()

    dispatch(actions.user.signInAsync(email, password, rememberMe));
    setShowLoading(true);
	}, [email, password, rememberMe])

  const content = <div className="row">
    <div className="col-0 col-md-5 text-center text-md-left">
      <img src="/images/login-illustration.jpg" alt="illustration" className="img-fluid" />
    </div>
    <div className="col-md-7">
      <h2 className="text-primary">Sign In</h2>
      <form onSubmit={handleSignIn}>
        <div className="form-group row">
          <label className="col-md-3 col-form-label">Email</label>

          <div className="col-md-8">
            <input 
              className="form-control"
              type="text" 
              name="email" 
              value={email} 
              onChange={genericTextChange(setEmail)}
              autoFocus={true}
            />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-md-3 col-form-label">Password</label>

          <div className="col-md-8">
            <input 
              className="form-control"
              type="password" 
              name="password" 
              value={password} 
              onChange={genericTextChange(setPassword)} 
            />

            <div className="text-right">
              <Link href="/forgot_password" className="text-dark">Forgot Password?</Link>
            </div>
          </div>
        </div>

        <div className="form-check mb-2">
          <input 
            className="form-check-input" 
            type="checkbox" 
            id="rememberMe"
            checked={rememberMe}
            onChange={genericCheckboxChange(setRememberMe)}
          />
          <label className="form-check-label" htmlFor="rememberMe">
            Remember Me?
          </label>
        </div>
        <button className="btn btn-primary">Login</button>
      </form>

    </div>
  </div>
  
  return (
    <Layout>
      <HeadTitle title="Sign In" />
      <TimeoutAlert
        onHide={() => setServerError(null)}
      >
        {serverError}
      </TimeoutAlert>
      
      <TimeoutAlert
        message={successMessage}
        onHide={() => setSuccessMessage(null)}
        alertColor="success"
      />

      <Breadcrumbs
        currentPage="Sign In"
      />
      
      {showLoading ? <Loading /> : content}
    </Layout>
  )
}

export default SignIn

export { basicGetStaticProps as getStaticProps } from '../lib/static_props'